import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const isSortOptionActive = (option, sortField, sortDirection) => {
  const field = option.value === 'message_count_asc' ? 'message_count' : option.value;
  return sortField === field && sortDirection === option.direction;
};

const InboxFiltersSidebar = ({
  t,
  sortDropdownRef,
  showSortDropdown,
  setShowSortDropdown,
  getCurrentSortOption,
  sortOptions,
  sortField,
  sortDirection,
  handleSortChange,
  handleClearFilters,
  visiblePriorityFilters,
  priorityCounts,
  selectedFilters,
  setSelectedFilters,
  hideSingleMessage,
  setHideSingleMessage
}) => (
  <aside className="flex h-full max-h-full w-64 flex-shrink-0 flex-col overflow-y-auto bg-neutral-100 px-4 py-6 pr-0 assistant-scrollbar min-h-0">
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">{t('assistant.filters.title')}</h3>
        <button
          type="button"
          onClick={handleClearFilters}
          className="link-action-xs font-semibold"
        >
          {t('assistant.filters.clearAll')}
        </button>
      </div>

      <div className="relative" ref={sortDropdownRef}>
        <h4 className="text-xs font-semibold text-gray-500 mb-2">{t('assistant.filters.sortBy')}</h4>
        <button
          type="button"
          onClick={() => setShowSortDropdown(!showSortDropdown)}
          className="form-dropdown-trigger form-dropdown-trigger-compact flex items-center justify-between transition-colors"
        >
          <span className="truncate">{getCurrentSortOption()?.label}</span>
          <ChevronDownIcon className={`w-4 h-4 transition-transform flex-shrink-0 ml-2 ${showSortDropdown ? 'rotate-180' : ''}`} />
        </button>
        {showSortDropdown && (
          <div className="absolute left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-20">
            <div className="py-1">
              {sortOptions.map((option) => (
                <button
                  key={`${option.value}-${option.direction}`}
                  type="button"
                  onClick={() => handleSortChange(option)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    isSortOptionActive(option, sortField, sortDirection)
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <h4 className="text-xs font-semibold text-gray-500 mb-2">{t('assistant.filters.priority')}</h4>
        <p className="text-xs text-gray-400 mb-3">{t('assistant.filters.priorityHint')}</p>
        <div className="space-y-1">
          {visiblePriorityFilters.map(({ value, label, Icon, color, dot }) => (
            <label key={value} className="flex items-start justify-between cursor-pointer group px-2 py-2 rounded-lg hover:bg-white transition-colors">
              <div className="flex items-start flex-1">
                <input
                  type="checkbox"
                  checked={selectedFilters.priority.includes(value)}
                  onChange={(e) => {
                    setSelectedFilters((prev) => ({
                      ...prev,
                      priority: e.target.checked
                        ? [...prev.priority, value]
                        : prev.priority.filter((v) => v !== value)
                    }));
                  }}
                  className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 border-gray-300 rounded"
                />
                <Icon className={`mt-0.5 ml-2.5 h-4 w-4 ${color}`} />
                <span className="ml-1.5 text-xs font-medium leading-5 text-gray-700">
                  {label} ({priorityCounts[value]})
                </span>
              </div>
              <span className={`mt-1 w-2 h-2 rounded-full ${dot}`} />
            </label>
          ))}
          {visiblePriorityFilters.length === 0 && (
            <p className="text-xs text-gray-400 py-1">{t('assistant.filters.noPriorities')}</p>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <h4 className="text-xs font-semibold text-gray-500 mb-2">{t('assistant.filters.display')}</h4>
        <label className="flex items-center cursor-pointer group px-2 py-2 rounded-lg hover:bg-white transition-colors">
          <span className="text-xs font-medium text-gray-700">{t('assistant.filters.hideSingleMessage')}</span>
          <div className="relative ml-4">
            <input
              type="checkbox"
              checked={hideSingleMessage}
              onChange={(e) => setHideSingleMessage(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600" />
          </div>
        </label>
      </div>
    </div>
  </aside>
);

export default InboxFiltersSidebar;
